import { useCallback, useContext, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { CSSProperties } from 'react';
import { GAME_HAND, HAND_TYPE } from 'constants/gameConstants';
import GameContext from 'contexts/GameContext';
import { useHandHistorySnapshotMode } from 'contexts/HandHistorySnapshotModeContext';
import { TableIdContext } from 'contexts/TableIdContext';
import { useIsTableOnScreen } from 'contexts/TableOnScreenContext';
import { Decimal } from 'decimal.js';
import gsap from 'gsap';
import { useOrientation } from 'hooks/useOrientation';
import { useTableSelector } from 'hooks/useTableSelector';
import { useTableSnap } from 'hooks/useTableSnap';
import * as _ from 'lodash';
import { TablePot } from 'reducers/currentTable';
import { tableMoneyFormatterCompact, toDecimal } from 'utils/functions';
import { convertToNumber } from 'utils/functions';

import { feltMetaText } from './feltTypography';
import { forkRowOffset, getRunKeys } from './HoldEmCards';
import PokerChip, { getChipColor } from './PokerChip';
import { getSeatPosition } from './Seats';
import { winChipAwardId } from './winChipAwards';
import WinChips from './WinChips';

import './Pot.scss';

/** One seat being paid from the pot, as <WinChips/> and the pill expect it. */
export type PotWinner = { player: string; win: number };

/**
 * Parse `game.runs` — "<totalRuns>,<stage>,<currentRun>" — returning null when
 * the hand isn't being run multiple times.
 */
export const parseRuns = (runs: unknown): { totalRuns: number; currentRun: number } | null => {
  const parts = String(runs ?? '').split(',');
  if (parts.length !== 3) return null;
  const totalRuns = Number(parts[0]);
  const currentRun = Number(parts[2]);
  if (!Number.isFinite(totalRuns) || totalRuns < 1) return null;
  if (!Number.isFinite(currentRun) || currentRun < 1) return null;
  return { totalRuns, currentRun };
};

/**
 * A pot's place in the hand: main pot 0, then SP(1), SP(2)...
 *
 * Read from the pot's OWN label rather than its position in the list, because
 * the list is not always in that order. `getPotsWithWinners` returns the pots
 * un-reversed below hand 13 and REVERSED from FIND_WINNERS on (texas/betting.js
 * ::getPotsWithWinners), so the showdown pays the smallest side pot first — the
 * array flips under the breakdown panel exactly when the hand reaches showdown.
 */
export const potRank = (label: unknown): number => {
  const match = /^SP\((\d+)\)$/.exec(String(label ?? ''));
  return match ? Number(match[1]) : 0;
};

/**
 * The seats one pot pays in the Hi/Lo phase being shown — who the chips fly to.
 *
 * `win > 0` is the load-bearing half. `populateWinAmount` floors each share to
 * the table's unit and hands the remainder to the first seats in dealer order,
 * so a split worth less than one unit per seat leaves a tied winner on `win: 0`
 * — in the pot's winner list, awarded nothing. A 2-chip pot split three ways in
 * a tournament does it. The payout sound and confetti already skip those
 * (tableSfx::potFlightWinnerSeats); without the same guard here the chips flew
 * to a seat whose stack never moved, silently.
 */
export const livePotWinners = (pot: any, handType: unknown): any[] => {
  const rows = Array.isArray(pot?.winners) ? pot.winners : [];
  return rows.filter(
    (f: any) =>
      Number(f?.win) > 0 && (f.handType === handType || (!f.handType && handType !== HAND_TYPE.LO))
  );
};

/**
 * Who is being paid for the board that is settling, and how much.
 *
 * `details` is written by predistpot as each board settles, so it is exact.
 * Before that — hand 13, winners known but not yet paid — it does not exist yet
 * and the live pot is used instead.
 *
 * Those live amounts used to be the WHOLE hand's chips, and were divided here by
 * the run count to compensate. They are not any more: getPotsWithWinners now
 * pays each board its own share server-side (shared/games/common/betting.js
 * ::currentRunPotShare), so dividing again would fly a third of a third.
 *
 * Doing it server-side is also the only way to be exact. The pot is split per
 * POT and the odd unit goes to the earliest board, which one division over the
 * total cannot reproduce: a $0.40 pot run three times pays 0.14 on board 1, not
 * 0.4 / 3 = 0.13, and table 674955 hand 31 credited seat 2 $228.34 where
 * 685 / 3 gives 228.33. The felt was a cent out even when it looked right.
 */
export const multiRunAwardWinners = (
  runWinners: any,
  currentRun: number,
  livePotWinners: any[]
): PotWinner[] => {
  // Validated, not just non-empty: a settled entry missing either field maps to
  // { player: 'undefined', win: NaN }, and because the array is still non-empty
  // the live fallback below is skipped — so one malformed row replaces the whole
  // board's winners with a seat that matches nobody and a NaN award, which then
  // reaches WinChips and the payout display.
  const details = runWinners?.[`run${currentRun}`]?.details;
  const settled = (Array.isArray(details) ? details : []).filter(
    // `d?.` covers a null row too: Number(undefined) is NaN, so it filters out
    // on the seat check without a separate guard.
    //
    // `win > 0` for the same reason `livePotWinners` uses it: a row awarding
    // nothing must not mount WinChips, which flies chips from the seat's mere
    // presence in this list and never reads the amount. predistpot already
    // drops those server-side (holdem-processor/texas/game.js::predistpot,
    // `.filter((d) => d.win > 0)`), so this is the client half of one rule
    // rather than a second opinion.
    (d: any) => Number.isFinite(Number(d?.seat)) && Number(d?.win) > 0
  );
  // `details` is BOARD-level: predistpot writes one row per seat paid anywhere on
  // this board, across every pot. The showdown walks MP, SP(1), SP(2)... and this
  // runs at each step, so using it as-is flew chips to every winner of the board
  // on every pot — including seats with no stake in the pot being paid. Scope it
  // to the pot the showdown is on; the live pot names that pot's winner for this
  // board, and the audio/confetti cue already reads the same source (tableSfx
  // ::potFlightWinnerSeats). Falls through to the live list when the two disagree.
  //
  // And take the AMOUNT from that live row, not from `details`. Being
  // board-level, a settled row holds the seat's total across every pot the
  // board paid it — so a seat that scoops the main pot AND a side pot has ONE
  // row carrying the sum, and this returned that sum at the main-pot step and
  // again at the side-pot step. The live row is per-pot and already this
  // board's own share (getPotsWithWinners::currentRunPotShare, server-side and
  // exact to the odd unit), which is the granularity the step is asking about.
  // `details` stays the gate — it is what says the board has settled — and the
  // fallback amount for a seat the live pot no longer lists.
  const liveBySeat = new Map<string, any>(
    (livePotWinners ?? []).map((w: any) => [String(w?.player), w])
  );
  const scoped =
    liveBySeat.size > 0 ? settled.filter((d: any) => liveBySeat.has(String(d.seat))) : settled;
  if (scoped.length > 0) {
    return scoped.map((d: any) => {
      const live = liveBySeat.get(String(d.seat));
      return { player: String(d.seat), win: Number(live ? live.win : d.win) };
    });
  }
  return (livePotWinners ?? []).map((w: any) => ({
    ...w,
    player: String(w.player)
  }));
};

/**
 * What the hand still has left to pay: the pot captured at the all-in, less
 * every board already settled. The pill can't be driven by the live pot here —
 * each settlement zeroes the bets it is derived from — so it would otherwise
 * read the full amount for the whole run-out and then drop to $0 at the end.
 *
 * `currentRunPaid` holds the board being settled back until its chips actually
 * launch. predistpot writes `potWon` at hand 13, BEFORE the showdown walk, so
 * counting it as soon as it exists dropped the pill a beat early — the felt
 * showed the pot already reduced while the winner pill was up and the chips
 * were still sitting in the middle. Boards before this one are always paid.
 */
export const multiRunPotRemaining = (
  runWinners: any,
  totalPot: number,
  currentRun: number,
  currentRunPaid: boolean
): number => {
  let paid = new Decimal(0);
  for (let run = 1; run <= (currentRunPaid ? currentRun : currentRun - 1); run++) {
    const won = Number(runWinners?.[`run${run}`]?.potWon);
    if (Number.isFinite(won) && won > 0) paid = paid.add(won);
  }
  return Math.max(0, new Decimal(totalPot).sub(paid).toDP(2).toNumber());
};

/**
 * One board's slice of a pot — the client-side mirror of the server's
 * `multiRunPotShare` (shared/games/common/betting.js): every board takes the
 * floor share and the first `remainder` boards take one extra minor unit, so
 * the boards sum back to the pot EXACTLY. A $0.40 pot run three times pays
 * 0.14 / 0.13 / 0.13, not 0.13 three times.
 *
 * `decimals` IS THE TABLE’S OWN PRECISION, not a constant 100.
 *
 * This used to hardcode cents, with a comment saying the client has no
 * `decimalPoints`. It does — `bank.decimalPoints` rides the `gotTexas`/`gotOmaha`
 * payload — and the server SETTLES at it: on a whole-chip table (0) a 7-chip pot
 * run twice pays 4 and 3, while a hardcoded 100 renders 3.50 and 3.50 against
 * that payout. The two numbers came from the same pot and disagreed.
 *
 * Defaults to 2, so a payload without the field behaves exactly as before.
 */
export const multiRunPotShare = (
  potTotal: number,
  totalRuns: number,
  runIndex: number,
  decimals = 2
): number => {
  if (!Number.isFinite(potTotal) || !(totalRuns > 0) || !(runIndex >= 1) || runIndex > totalRuns) {
    return 0;
  }
  // `?? 2` and a finite check for the same reason the server applies one: a
  // NULL `decimalPoints` column arrives as null, and `Number(null)` is 0 — which
  // would print whole chips against a penny settlement, the mirror of the bug
  // this parameter exists to fix.
  const dp = Number.isFinite(decimals) && decimals >= 0 ? Math.floor(decimals) : 2;
  const factor = 10 ** dp;
  // Same never-round-up conversion as the server, so the felt cannot show a
  // unit the payout does not pay.
  const units = Math.floor(Number((potTotal * factor).toFixed(6)));
  const base = Math.floor(units / totalRuns);
  const remainder = units - base * totalRuns;
  return (base + (runIndex <= remainder ? 1 : 0)) / factor;
};

/**
 * What one MP/SP row reads while the boards are being PAID.
 *
 * `runInfo` is null whenever the boards are still running out — the rows then
 * show the whole contested pot, which is what is actually still in the middle.
 * Once a board starts paying, each row drops to that board's own slice, and to
 * zero as the showdown walks past it: `potIdx` indexes `pots` in the order the
 * showdown walks them, so anything before `currentPot` is already paid, and the
 * pot being walked is paid from step 3 — the same boundary the TOTAL POT pill
 * and the chip flight use.
 *
 * `hiPaid` — Omaha Hi/Lo only, and only for the pot being walked: see
 * hiPaidLoOwed. While it is set the pot is half-paid, so the row shows the Lo
 * half still owed instead of the step-3 zero. `null` everywhere else.
 */
export const sidePotRunAmount = (
  potTotal: number,
  runInfo: { totalRuns: number; currentRun: number } | null,
  potIdx: number,
  currentPot: number,
  currentStep: number,
  decimals = 2,
  hiPaid: number | null = null
): number => {
  if (!runInfo) return potTotal;
  if (potIdx < currentPot) return 0;
  const share = multiRunPotShare(potTotal, runInfo.totalRuns, runInfo.currentRun, decimals);
  if (potIdx === currentPot && hiPaid !== null) {
    return Math.max(0, new Decimal(share).sub(hiPaid).toNumber());
  }
  return potIdx === currentPot && currentStep > 2 ? 0 : share;
};

/**
 * What a Hi/Lo split pot has paid while its Lo half is still OWED — `null` when
 * the pot is not in that half-paid window.
 *
 * The Omaha engine walks a split pot TWICE: Hi to step 3 (pays the Hi half),
 * then flips `handType` to Lo and restarts at step 1 on the SAME pot
 * (omaha-processor showdownMoveNext). Step alone read "paid" at the Hi step 3
 * and "unpaid" again at Lo step 1, so the row went 10 -> 0 -> 10 -> 0 instead of
 * 10 -> 5 -> 0 (EN-10910). The window is Hi step 3+ through Lo step 2.
 *
 * "Has a Lo winner" is the engine's own test for a second walk (hasLowWinners),
 * and only Omaha Hi/Lo pots carry Lo winners — Hold'em always gets `null`.
 */
export const hiPaidLoOwed = (pot: any, handType: unknown, currentStep: number): number | null => {
  const rows: any[] = Array.isArray(pot?.winners) ? pot.winners : [];
  if (!rows.some((w) => w?.handType === HAND_TYPE.LO)) return null;
  const onLo = handType === HAND_TYPE.LO;
  const hiPaid = onLo || currentStep > 2;
  const loPaid = onLo && currentStep > 2;
  if (!hiPaid || loPaid) return null;
  return rows
    .filter((w) => w?.handType === HAND_TYPE.HI && Number(w?.win) > 0)
    .reduce((t: Decimal, w) => t.add(Number(w.win)), new Decimal(0))
    .toNumber();
};

/**
 * Which board the MP/SP rows are showing — `null` until the first payout, so
 * the breakdown reads as the whole contested pot while the boards deal.
 *
 * `isShowdown` cannot draw this line: it is on from hand 13, and hand 13 is
 * where every board is dealt (AC-6.3 pre-deal), so the RUN caption and the
 * per-board slices came up while the run-outs were still landing. Payout 1 is
 * hand 14 — predistpot writes it in the same statement that settles board 1.
 *
 * `currentRun > 1` holds the breakdown across the beat between boards:
 * RunMultiple drops `hand` back to 13 to take the next board, and without this
 * the rows would flash back to the full pot between payouts.
 */
export const multiRunPayoutInfo = (
  isMultiRunActive: boolean,
  runs: unknown,
  hand: unknown
): { totalRuns: number; currentRun: number } | null => {
  if (!isMultiRunActive) return null;
  const runInfo = parseRuns(runs);
  if (!runInfo) return null;
  const paying = +(hand as any) >= GAME_HAND.PAY_WINNERS || runInfo.currentRun > 1;
  return paying ? runInfo : null;
};

/**
 * Which pot figure the felt shows: the single pill, or the MP/SP breakdown.
 *
 * A run-it-twice hand settles ONE BOARD AT A TIME, so `isShowdown` turns on and
 * off once per run. Driving this choice off it alone made the felt swap the
 * breakdown for the single pill and back on every board — the flicker seen on
 * runs 2 and 3. The pot STRUCTURE is fixed at the all-in and is identical for
 * every board (the server replays it from `runWinners.pots`), so once a
 * multi-run hand has side pots the breakdown holds for the whole run-out.
 *
 * A normal hand is unchanged: at showdown the pill names the pot being paid
 * (MP / SP(n)) as the showdown walks them, and the stack steps aside.
 *
 * Both can be false — a showdown pot with no label — which is the state the pill
 * was already written to blank out, so it stays reachable rather than being
 * quietly "fixed" here.
 */
export const potVisibility = (
  potCount: number,
  isShowdown: boolean,
  isMultiRun: boolean,
  showdownPotHasLabel: boolean
): { pill: boolean; breakdown: boolean } => {
  const holdBreakdown = isMultiRun && potCount >= 2;
  return {
    pill: !holdBreakdown && (potCount <= 1 || (isShowdown && showdownPotHasLabel)),
    breakdown: potCount >= 2 && (!isShowdown || holdBreakdown)
  };
};

/**
 * How far the main-pot pill shifts to sit beside the board being run NOW.
 *
 * A run-it-twice fork stacks one row per run, centred on the board's own middle,
 * so the live board sits `index - (count - 1) / 2` SETTLED rows away from that
 * centre — settled, because exactly one run is live, so every row between the
 * live one and the centre is a settled (shrunk) one, and the live row's own
 * height cancels out of the sum. Times --seat-scale because the board is drawn
 * scaled and these are board pixels, not pill pixels. `''` when there is nothing
 * to shift (one board, or a run not on the felt yet).
 *
 * Only the MAIN-pot pill moves: the MP/SP stack is a list, and reads better
 * centred on the whole fork.
 */
export const multiRunRowOffset = (
  runKeys: string[],
  currentRun: number,
  isPortrait: boolean
): string => {
  const index = runKeys.indexOf(`run${currentRun}`);
  if (index < 0 || runKeys.length < 2) return '';
  return forkRowOffset(index - (runKeys.length - 1) / 2, isPortrait, runKeys.length);
};

/**
 * One MP/SP row's figure, rolling between values the way the main pill does —
 * so a pot being paid visibly counts down to zero instead of blinking out.
 *
 * Snaps instead of rolling on the first value and on arrival (table switch,
 * reconnect resync), for the same reason the pill does: the figure is already
 * at its current size, nothing "happened" for the player to watch.
 */
const SidePotAmount = ({ value, tableType }: { value: number; tableType?: string }) => {
  const shouldSnap = useTableSnap();
  const isHandHistorySnapshot = useHandHistorySnapshotMode();
  const numberRef = useRef<{ value: number }>({ value });
  const tweenRef = useRef<gsap.core.Tween | null>(null);
  const prevRef = useRef<number | null>(null);
  const [display, setDisplay] = useState(value);

  useEffect(() => {
    tweenRef.current?.kill();

    const prev = prevRef.current;
    prevRef.current = value;
    // `shouldSnap()` read inside the effect, never as a dependency — see
    // useTableSnap.
    if (isHandHistorySnapshot || prev === null || prev === value || shouldSnap()) {
      numberRef.current.value = value;
      setDisplay(value);
      return undefined;
    }

    numberRef.current.value = prev;
    tweenRef.current = gsap.to(numberRef.current, {
      value,
      duration: 0.8,
      ease: 'none',
      snap: { value: 0.01 },
      onUpdate: () => setDisplay(numberRef.current.value)
    });

    return () => {
      tweenRef.current?.kill();
    };
  }, [isHandHistorySnapshot, value]);

  return <>{tableMoneyFormatterCompact(display, tableType)}</>;
};

const Pot = () => {
  const shouldSnap = useTableSnap();
  const { pots, showdown, gameNo } = useTableSelector((table) => table.game);
  const isHandHistorySnapshot = useHandHistorySnapshotMode();
  const { isShowdown } = useTableSelector((table) => table.gameState);
  const players = useTableSelector((table) => table.players);
  const currentPlayerSeatId = useTableSelector((table) => table.currentPlayer.seatId);
  const { seatPositions } = useContext(GameContext);
  const isPortrait = useOrientation() === 'portrait';

  const tableId = useContext(TableIdContext);
  const isTableOnScreen = useIsTableOnScreen();
  const [playerWinners, setPlayerWinners] = useState<any[]>([]);
  // A payout animation is a live transition, not a property of a snapshot.
  // Remember the current hand only after this mounted table has observed it in
  // progress. A table opened/rejoined during showdown therefore renders the
  // settled stacks without replaying the pot flight from the snapshot.
  const observedLiveGameRef = useRef<string | null>(null);

  const isMultiRunActive = useTableSelector((table) => table.currentPlayer.isMultiRunActive);
  // Full hand pot, captured once on run 1 before the per-run settlement drains
  // the live bets (see holdem-processor predistpot). Needed because `pots` is
  // derived from those bets, so from run 2 on it comes through empty.
  const multiRunTotalPot = useTableSelector((table) => table.game.runWinners?.totalPot);
  // Per-board winners + payouts, and which board is settling.
  const runWinners = useTableSelector((table) => table.game.runWinners);
  const runs = useTableSelector((table) => table.game.runs);
  // Payout starts at hand 14; hand 13 is where the boards are dealt.
  const hand = useTableSelector((table) => table.game.hand);
  // Which run-outs are on the felt — the main-pot pill lines up with the live one.
  const multiRunCards = useTableSelector((table) => table.game.multiRunCards);

  const tableType = useTableSelector((table) => table.game?.tabletype);
  // The table’s money precision, as the SERVER settles the multi-run split at
  // (`bank.decimalPoints`). Undefined on a payload that predates it, which the
  // share helper reads as the historical 2.
  const decimalPoints = useTableSelector((table) => (table.game as any)?.decimalPoints);

  const { setPotPosition } = useContext(GameContext);
  const potRef = useRef<HTMLDivElement>(null);
  // The MP/SP breakdown: where a multi-run payout's chips launch in landscape
  // while the pill is hidden (EN-10919, see chipOrigin below).
  const sidePotStackRef = useRef<HTMLDivElement>(null);
  const potScaleRef = useRef<gsap.core.Tween | null>(null);
  const potNumberRef = useRef<{ value: number }>({ value: 0 });
  const deadMoney = useTableSelector((table) => table.game.deadMoney);

  // Publish pot's center position to GameContext for chip-flying-to-pot
  // animations. Layout reads in the dep array previously forced a layout
  // flush on every render; trigger on isMultiRunActive (which actually shifts
  // pot position) and on first mount instead.
  useLayoutEffect(() => {
    const el = potRef.current;
    if (!el) return;
    setPotPosition({
      left: el.offsetLeft + el.offsetWidth / 2,
      top: el.offsetTop + el.offsetHeight / 2
    });
  }, [setPotPosition, isMultiRunActive]);

  const [newPotValue, setNewPotValue] = useState(0);
  const [potFloat, setPotFloat] = useState<any>(convertToNumber(newPotValue));

  // Tracks the previous pot value to fire the bounce only when the pot
  // actually grows. Initialized to `null` so the first render after a
  // mount/remount/resize has no baseline and doesn't mistake the initial
  // value (e.g. an already-large mid-hand pot) for a fresh increase.
  const prevPotValueRef = useRef<number | null>(null);

  // Only bounce when pot grows
  const resetPotScale = useCallback(
    _.debounce(() => {
      if (potRef.current) {
        gsap.to(potRef.current, { scale: 1, duration: 0.3, ease: 'power2.out' });
      }
    }, 300),
    []
  );

  // Kill any in-flight pot tweens (bounce / multi-run move) and cancel the
  // pending debounce on unmount — otherwise a table that unmounts mid-bounce
  // orphans the tween against a detached pot node (leak source).
  useEffect(() => {
    const el = potRef.current;
    return () => {
      if (el) gsap.killTweensOf(el);
      resetPotScale.cancel();
    };
  }, [resetPotScale]);

  useEffect(() => {
    // A completed-hand snapshot shows the resolved pot: its winner rows label
    // the result, they are not a payout in progress to count down.
    const totalWin = isHandHistorySnapshot
      ? 0
      : pots[showdown.currentPot]?.winners
          ?.filter(
            (f: any) =>
              (f.handType === HAND_TYPE.HI && showdown.handType === HAND_TYPE.LO) ||
              (f.handType === showdown.handType && +showdown.currentStep > 2) ||
              (!f.handType && +showdown.currentStep > 2)
          )
          .map((f: any) => +f.win)
          .reduce((t: any, num: any) => t + num, 0) ?? 0;

    const liveValue = Decimal.sub(
      Decimal.sum(pots[showdown.currentPot]?.pot ?? 0, deadMoney),
      totalWin
    )
      .toDP(2)
      .toNumber();

    // Multi-run: every board plays for a slice of the pot captured at the all-in,
    // and each settlement zeroes the bets `pots` is derived from. Count down from
    // the captured total as each board is paid, so the pill tracks what the hand
    // still owes instead of showing the whole amount for the entire run-out.
    const runInfo = parseRuns(runs);
    const durableTotal = Number(multiRunTotalPot);
    const hasDurableTotal =
      isMultiRunActive && !!runInfo && Number.isFinite(durableTotal) && durableTotal > 0;
    // Omaha Hi/Lo: between the Hi and Lo payouts the board is half-paid, not
    // paid-then-unpaid — see hiPaidLoOwed (EN-10910).
    const hiPaid = hiPaidLoOwed(
      pots[showdown.currentPot],
      showdown.handType,
      +showdown.currentStep
    );
    const newValue = hasDurableTotal
      ? // Step 3 is when chips leave the pot (see setPlayerWinners below and
        // useAnimatedWinStack), so the board being settled only counts from there.
        Math.max(
          0,
          new Decimal(
            multiRunPotRemaining(
              runWinners,
              durableTotal,
              runInfo!.currentRun,
              hiPaid === null && +showdown.currentStep > 2
            )
          )
            .sub(hiPaid ?? 0)
            .toNumber()
        )
      : liveValue;

    setNewPotValue(newValue);

    // Chips launch at step 3, so anything below it clears. NOT conditioned on the
    // pot existing any more: when `pots[currentPot]` misses, the clear used to be
    // skipped and the multi-run branch below fell back to the whole BOARD's
    // winners with no pot to scope them to — the same over-firing this guard is
    // here to prevent. No pot to pay means no chips. Kept in lockstep with
    // useAnimatedWinStack::seatWinTotal, which gates the seat's count-up.
    const handPhase = Number(hand);
    const gameIdentity =
      gameNo === null || gameNo === undefined || String(gameNo) === '' ? null : String(gameNo);
    // The table surfaces stay mounted while parked behind the lobby or another
    // table. Do not let that hidden component qualify a hand for a later chip
    // flight: on return, an award already in progress is snapshot state, not a
    // transition the player watched. A subsequent hand becomes eligible only
    // after this surface observes it in progress while actually on screen.
    if (!isTableOnScreen) {
      observedLiveGameRef.current = null;
    } else if (
      gameIdentity !== null &&
      Number.isFinite(handPhase) &&
      handPhase < GAME_HAND.FIND_WINNERS
    ) {
      observedLiveGameRef.current = gameIdentity;
    }
    const observedThisHandLive =
      gameIdentity !== null && observedLiveGameRef.current === gameIdentity;
    const awarding =
      !Number.isFinite(handPhase) ||
      (handPhase >= GAME_HAND.FIND_WINNERS &&
        handPhase < GAME_HAND.RECORD_NEW_STATS_AND_START_NEW_HAND);
    const live = livePotWinners(pots[showdown.currentPot], showdown.handType);
    // Pay out board by board: prefer the settled board's own `details`, and
    // fall back to the live pot, which the server has already reduced to this
    // board's share. See multiRunAwardWinners.
    const winners =
      isMultiRunActive && runInfo
        ? multiRunAwardWinners(runWinners, runInfo.currentRun, live)
        : live;

    if (!isTableOnScreen || !observedThisHandLive || !awarding || showdown.currentStep < 3) {
      setPlayerWinners([]);
    } else {
      setPlayerWinners(
        winners.map((winner: any) => ({
          ...winner,
          awardId: winChipAwardId(
            tableId,
            gameNo,
            showdown.currentPot,
            winner.player,
            winner.handType || showdown.handType,
            runs
          )
        }))
      );
    }

    // Only bounce when the pot actually grew between renders. Skip the very
    // first render after mount (prev === null) so resize-driven remounts /
    // unrelated parent re-renders don't trigger the bounce.
    // `!shouldSnap()`: a pot that grew while the player was on another table
    // (or during a reconnect resync) must not bounce on arrival.
    const prevPotValue = prevPotValueRef.current;
    prevPotValueRef.current = newValue;
    if (
      !isHandHistorySnapshot &&
      prevPotValue !== null &&
      potRef.current &&
      newValue > prevPotValue &&
      !shouldSnap()
    ) {
      gsap.to(potRef.current, { scale: 1.1, duration: 0.25, ease: 'power2.out' });
      resetPotScale();
    }
  }, [
    deadMoney,
    isHandHistorySnapshot,
    isMultiRunActive,
    multiRunTotalPot,
    pots,
    resetPotScale,
    runWinners,
    runs,
    showdown.currentStep,
    showdown.currentPot,
    // Read in the `totalWin` filter above and by `livePotWinners` below, and it
    // moves on its own: a Hi/Lo showdown flips Hi -> Lo with `currentPot` and
    // `currentStep` unchanged, and immer leaves `pots` referentially stable when
    // a frame touches only `showdown`. Without it the pill and the chip-flight
    // recipients stayed on the Hi phase.
    showdown.handType,
    gameNo,
    tableId,
    hand,
    isTableOnScreen
  ]);

  // Animated number tick (matches Unity UIPot: 10-step over 0.8s). Uses
  // `prevNewPotValueRef` (initialized to null) so we can distinguish a real
  // pot change during play from an initial-value materialization:
  //   • prev === null   → first render after mount/remount → snap, no tween
  //   • prev === 0      → coming from an empty pot (between hands / initial
  //                       fill from server) → snap so the digits don't roll
  //                       up from 0 every time the component re-renders
  //   • prev > 0        → real change during play → animate from prev to new
  const prevNewPotValueRef = useRef<number | null>(null);
  useEffect(() => {
    if (potScaleRef.current) {
      potScaleRef.current.kill();
    }

    const prev = prevNewPotValueRef.current;
    prevNewPotValueRef.current = newPotValue;

    // `shouldSnap()`: on arrival (table switch, post-login/reconnect restore)
    // the pot is already at its current size — show the final figure instead of
    // rolling the digits up to it.
    if (isHandHistorySnapshot || prev === null || prev === 0 || shouldSnap()) {
      potNumberRef.current.value = newPotValue;
      setPotFloat(newPotValue.toFixed(2));
      return;
    }

    potNumberRef.current.value = prev;
    potScaleRef.current = gsap.to(potNumberRef.current, {
      value: newPotValue,
      duration: 0.8,
      ease: 'none',
      snap: { value: 0.01 },
      onUpdate: () => {
        setPotFloat(potNumberRef.current.value.toFixed(2));
      }
    });

    return () => {
      if (potScaleRef.current) {
        potScaleRef.current.kill();
      }
    };
  }, [isHandHistorySnapshot, newPotValue]);

  // Dynamic chip count: 1 chip minimum, grows as pot increases
  // Each "level" roughly doubles (1 chip at start, up to 6 at large pots)
  const chipCount = Math.min(Math.max(1, Math.floor(Math.log2(Math.max(newPotValue, 1)) + 1)), 6);

  // Lay side pots out in up to two columns. A full all-in can produce ~9 pots,
  // which in one column would run past the felt; splitting into two columns
  // halves the height so they fit at full (legible) font size instead of being
  // scaled down. One column for the common case (<= MAX_POTS_PER_COLUMN).
  const MAX_POTS_PER_COLUMN = 5;
  const potColumnCount = pots.length > MAX_POTS_PER_COLUMN ? 2 : 1;
  const potsPerColumn = Math.ceil(pots.length / potColumnCount);
  // Ordered by each pot's OWN rank, not its array position: the server reverses
  // the list at showdown, which used to rename MP <-> SP(n) under the panel and
  // move the dead money onto whichever pot happened to land first. Sorted before
  // slicing so the columns split the same way all hand.
  const rankedPots = [...pots]
    .map((p: TablePot, idx: number) => ({ pot: p.pot, label: p.label, idx }))
    .sort((a, b) => potRank(a.label) - potRank(b.label))
    // Dead money belongs to the main pot, and to exactly one row. Taken as the
    // FIRST pot once ranked rather than by label match, so a label this doesn't
    // recognise (a demo fixture, a future rename) still puts it somewhere sane
    // instead of on every row that ranks 0.
    .map((p, i) => ({ ...p, isMainPot: i === 0 }));
  const potColumns = Array.from({ length: potColumnCount }, (_, col) =>
    rankedPots.slice(col * potsPerColumn, (col + 1) * potsPerColumn)
  );

  // At showdown the pot being paid names itself (MP / SP(n)). Otherwise, during
  // a multi-run, the pill can only be showing the whole-hand pot — with 2+ pots
  // it's hidden in favour of the side-pot stack — so it says so, rather than
  // appearing to contradict the smaller per-board slices beside each run.
  // A multi-run keeps the TOTAL POT wording even at showdown. The pot's own
  // label (MP / SP(n) / Pot) otherwise won exactly when it matters most, and
  // reading "Pot $0.40" beside boards paying $0.14 each looks contradictory —
  // it is the whole hand's pot counting down, not one board's.
  const showdownPotLabel = isShowdown ? pots[showdown.currentPot]?.label : '';
  const potLabel = isMultiRunActive ? 'TOTAL POT' : showdownPotLabel || 'POT';

  // One pot → the pill; side pots → the MP/SP breakdown. A multi-run hand holds
  // the breakdown across every board instead of swapping per settlement.
  const potShow = potVisibility(
    pots.length,
    isShowdown,
    isMultiRunActive,
    !!pots[showdown.currentPot]?.label
  );

  // The MP/SP rows carry the WHOLE contested pot while the boards are still
  // running out — that is what is in the middle, and it is identical for every
  // board. Once a board starts PAYING they switch to that board's own slice,
  // captioned RUN n so the smaller figures read as one board's share rather
  // than looking like the pot shrank. `null` = still running the boards.
  // The caption rides the SAME gate, deliberately: it names the board a figure
  // belongs to, so it must not appear while the figures are still the whole
  // hand's pot. Captioning the run-out "RUN 1 OF 3" over the total read as a
  // per-board figure that had not shrunk. While the boards deal the felt says
  // TOTAL POT and nothing else; the caption and the per-board slices arrive
  // together at payout 1.
  const payoutRunInfo = multiRunPayoutInfo(isMultiRunActive, runs, hand);

  // With a run-it-twice fork the main-pot pill rides the board being run now,
  // instead of floating at the middle of the stack. The MP/SP list below stays
  // centred on the whole fork.
  const rowOffset = multiRunRowOffset(
    getRunKeys(multiRunCards, runs),
    parseRuns(runs)?.currentRun ?? 0,
    isPortrait
  );
  const rowShift = rowOffset ? ` translateY(${rowOffset})` : '';

  // The main pill (single pot / showdown). Portrait: in-flow above the cards.
  // Landscape: anchored beside the board column, never over it, and scaled
  // around the edge facing the cards so it hugs them as the table scales —
  // the layout /demo-table shows, since it drives this same component.
  const pillWrapperStyle: CSSProperties = isPortrait
    ? potScaleWrapperStyle
    : {
        ...potScaleWrapperStyle,
        position: 'absolute',
        left: 'calc(100% + 10px)',
        transformOrigin: 'left center',
        top: '50%',
        transform: `translateY(-50%)${rowShift} scale(var(--seat-scale, 1))`
      };

  const sidePotStackStyle: CSSProperties = isPortrait
    ? sidePotStackPortraitStyle
    : sidePotStackLandscapeStyle;

  // Where the chips fly FROM on their way to a winner: the pot the player can
  // see (EN-10919). A multi-run hand with side pots hides the pill and holds the
  // MP/SP breakdown across every board. In landscape the hidden pill is still
  // parked beside the board column, right of the run boards, so the chips used
  // to launch from empty felt there. Portrait keeps the pill: it sits in-flow
  // just above the breakdown, which is what QA passed.
  const chipOrigin =
    !isPortrait && !potShow.pill && sidePotStackRef.current
      ? sidePotStackRef.current
      : potRef.current;

  return (
    // Landscape: stay non-positioning so the side pots anchor to the centered
    // board column (left of the cards). Portrait: relative is fine (pill/side
    // pots are in-flow there).
    <div style={{ position: isPortrait ? 'relative' : 'static' }}>
      {/* Responsive wrapper: scales the pot pill with the table (--seat-scale)
          without touching potRef, which GSAP animates (bounce/move). Landscape
          anchors it to the right of the cards; portrait keeps it in-flow above. */}
      {/* `data-pot-cluster` on both pot boxes: the winner banner measures what
          is overhead of the board. In portrait these stack above it and set how
          high the banner may rise (TableWinnerMessage::bannerRowShift). */}
      <div style={pillWrapperStyle} data-pot-cluster=''>
        <div
          data-pot-target=''
          data-pot-collection-target={isPortrait || potShow.pill ? '' : undefined}
          style={{
            ...potContainerStyle,
            opacity: potShow.pill ? 1 : 0
          }}
          ref={potRef}>
          {/* Chip stack — grows with pot size, color by value */}
          <div style={chipStackStyle}>
            {Array.from({ length: chipCount }).map((_, i) => (
              <PokerChip
                key={i}
                size={18}
                color={getChipColor(newPotValue / Math.max(chipCount - i, 1))}
                style={{
                  position: 'absolute',
                  top: -i * 3,
                  left: 0,
                  filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.3))'
                }}
              />
            ))}
          </div>
          {/* Amount */}
          <div style={potInfoStyle}>
            {payoutRunInfo && !potShow.breakdown && (
              <span style={runCaptionStyle}>
                RUN {payoutRunInfo.currentRun} OF {payoutRunInfo.totalRuns}
              </span>
            )}
            <span style={potLabelStyle}>{potLabel}</span>
            <span style={potAmountStyle}>
              {tableMoneyFormatterCompact(tableType === 's' ? potFloat : +potFloat, tableType)}
            </span>
          </div>
        </div>
      </div>

      {potShow.breakdown && (
        <div
          ref={sidePotStackRef}
          style={sidePotStackStyle}
          className='sidepot-container'
          data-pot-collection-target={!isPortrait && !potShow.pill ? '' : undefined}
          data-pot-cluster=''>
          {/* The caption over the MP/SP rows names what those figures ARE, so it
              switches with them rather than appearing alongside them: the whole
              hand's pot while the boards run out, then the board being paid.
              This is the only place the TOTAL POT wording reaches a side-pot
              hand — `potVisibility` hides the pill that otherwise carries it
              whenever a multi-run hand has more than one pot. */}
          {isMultiRunActive && (
            <span style={runCaptionStyle}>
              {payoutRunInfo
                ? `RUN ${payoutRunInfo.currentRun} OF ${payoutRunInfo.totalRuns}`
                : 'TOTAL POT'}
            </span>
          )}
          <div
            style={{
              ...sidePotColumnsRowStyle,
              // Portrait has no board to sit beside, so the columns read
              // left-to-right as written.
              flexDirection: isPortrait ? 'row' : 'row-reverse'
            }}>
            {potColumns.map((column, colIdx) => (
              <div key={colIdx} style={sidePotColumnStyle}>
                {column.map(({ pot, label, isMainPot, idx }) => {
                  // Dead money belongs to the MAIN pot — identified by its label,
                  // for the same reason as the label itself.
                  const totalPot = isMainPot
                    ? tableType !== 's'
                      ? +toDecimal(Decimal.sum(pot, deadMoney).toNumber())
                      : Decimal.sum(pot, deadMoney).toDP(2).toNumber()
                    : pot;

                  return (
                    // Keyed by LABEL, not array index: the server reverses the
                    // pot list at showdown, so index 0 stops meaning MP exactly
                    // when the rows start counting down — and the count-down
                    // would tween across rows.
                    // `display: contents` so the label and the figure are the
                    // column grid's own cells: every label shares one track and
                    // every figure another, which is what lines the rows up.
                    <div key={label ?? idx} style={rowContentsStyle}>
                      <span className='text' style={sidePotLabelStyle}>
                        {label}:
                      </span>
                      <span className='text' style={sidePotAmountCellStyle}>
                        <SidePotAmount
                          value={sidePotRunAmount(
                            totalPot,
                            payoutRunInfo,
                            idx,
                            showdown.currentPot,
                            +showdown.currentStep,
                            decimalPoints,
                            hiPaidLoOwed(
                              pots[showdown.currentPot],
                              showdown.handType,
                              +showdown.currentStep
                            )
                          )}
                          tableType={tableType}
                        />
                      </span>
                    </div>
                  );
                })}
              </div>
            ))}
          </div>
        </div>
      )}

      {!isHandHistorySnapshot &&
        playerWinners.map((winner: any) => {
          const playerSeatPosition = getSeatPosition(
            seatPositions,
            players,
            +winner.player,
            currentPlayerSeatId
          );
          return (
            playerSeatPosition && (
              <WinChips
                key={winner.awardId ?? `win-chip-${winner.player}`}
                awardId={winner.awardId}
                seatId={+winner.player}
                originElement={chipOrigin}
                seatPosition={playerSeatPosition}
                potValue={+winner.win}
              />
            )
          );
        })}
    </div>
  );
};

export default Pot;

// Scales the central pot pill with the table. transformOrigin center so the
// pill's center (the chip-fly target) stays put as it scales.
const potScaleWrapperStyle: CSSProperties = {
  display: 'inline-flex',
  // Scale the whole pill with the table so it gets smaller on small screens;
  // the amount/label keep their own readable floor (see potAmountStyle) so the
  // pot stays legible even as the chip and padding shrink.
  transform: 'scale(var(--seat-scale, 1))',
  transformOrigin: 'center center',
  // Anchors the run badge above the pill without it joining this row.
  position: 'relative'
};

const potContainerStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: '0.4rem',
  zIndex: 2,
  whiteSpace: 'nowrap',
  padding: '0.3rem 0.6rem 0.3rem 0.4rem',
  borderRadius: '10px',
  border: '1px solid rgba(255,255,255,0.08)',
  backgroundColor: 'rgba(0,0,0,0.2)'
};

const chipStackStyle: CSSProperties = {
  position: 'relative',
  width: 22,
  height: 22
};

const potInfoStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start'
};

// "POT" / side-pot caption — a felt LABEL, so it takes the shared felt meta
// treatment (family, translucency, shadow) rather than its own. EN-10520.
const potLabelStyle: CSSProperties = {
  ...feltMetaText,
  // Counter-scales the wrapper so the "POT" label never renders below ~8px, then
  // scales with the player's Table View Size.
  // See Bet.tsx: the view scale lives in `--seat-scale` now.
  fontSize: 'max(0.62rem, calc(8px / var(--seat-scale, 1)))',
  fontWeight: 600,
  letterSpacing: '0.1em',
  lineHeight: 1
};

// The pot AMOUNT is the one deliberate exception to the felt's translucent
// treatment: it's content, not context, so it keeps the brand accent. It still
// shares the felt family and shadow so the pill reads as part of the same
// system.
const potAmountStyle: CSSProperties = {
  ...feltMetaText,
  // Counter-scales the wrapper so the amount stays readable (~10px floor) even
  // as the pill shrinks; also scales with the player's Table View Size.
  fontSize: 'max(0.92rem, calc(10px / var(--seat-scale, 1)))',
  fontWeight: 600,
  letterSpacing: 'normal',
  color: '#E98C26',
  lineHeight: 1.2,
  textShadow: '0 1px 3px rgba(0,0,0,0.4)'
};

// Just the pot label (MP / SP(n)) — lightly emphasised, not heavy bold. Carries
// its own colon so it can be a grid cell rather than inline text.
const sidePotLabelStyle: CSSProperties = {
  fontWeight: 600,
  color: 'rgba(255,255,255,0.9)',
  whiteSpace: 'nowrap',
  textAlign: 'left'
};

// The figure track: right-aligned, so the amounts line up on their last digit
// however wide the label beside them is.
const sidePotAmountCellStyle: CSSProperties = {
  textAlign: 'right',
  whiteSpace: 'nowrap'
};

// Lets a row's two spans be cells of the column's grid — see the row comment.
const rowContentsStyle: CSSProperties = { display: 'contents' };

// One column of pot rows: a two-track grid (label | figure) so every label
// shares one edge and every figure the other, instead of each row being its own
// pill sized to its own text.
const sidePotColumnStyle: CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'auto auto',
  columnGap: 6,
  rowGap: 2,
  alignItems: 'center'
};

// The breakdown is ONE block, not one per row: per-row pills each sized to their
// own text, so the stack came out ragged on both edges and the RUN caption read
// as a third pot row. A single panel gives the rows a shared frame; the grid
// inside gives them shared columns. No background: the rows sit straight on the
// felt, so the text carries its own shadow for contrast.
const sidePotPanelStyle: CSSProperties = {
  display: 'flex',
  // Column so the RUN n caption sits ABOVE the pot rows.
  flexDirection: 'column',
  // Centred: the caption is a heading for the panel, not another pot row.
  alignItems: 'center',
  gap: 1,
  padding: '0.15rem 0.55rem',
  textShadow: '0 1px 3px rgba(0,0,0,0.9)',
  whiteSpace: 'nowrap'
};

// Landscape: anchored to the centered board column (the Pot wrapper is
// non-positioning here), so right:100% is the board's left edge — the stack sits
// just left of the cards and tracks the board's size/position instead of a fixed
// pixel offset. Row of columns (row-reverse so the first pots stay nearest the
// cards); extra columns grow leftward, away from the board.
const sidePotStackLandscapeStyle: CSSProperties = {
  ...sidePotPanelStyle,
  position: 'absolute',
  right: 'calc(100% + 10px)',
  top: '50%',
  transform: 'translateY(-50%)',
  zIndex: 2
};

// The pot rows themselves. Row-reverse so the first pots stay nearest the
// cards; extra columns grow leftward, away from the board.
const sidePotColumnsRowStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'row-reverse',
  alignItems: 'flex-start',
  gap: 8
};

// "RUN n OF m" — which board the figures beneath belong to, and how many boards
// the hand is running. Felt meta treatment and
// the pot amount's accent, so it reads as a heading for the rows, not a pot.
const runCaptionStyle: CSSProperties = {
  ...feltMetaText,
  fontSize: '0.7rem',
  fontWeight: 700,
  letterSpacing: '0.12em',
  color: '#E98C26',
  lineHeight: 1.2
};

// Portrait stacks Pot above the cards (a column). Render the side pots in-flow
// (not absolute) so they take their own space in the Pot slot and sit directly
// above the community cards instead of overlapping the top of the board.
const sidePotStackPortraitStyle: CSSProperties = {
  ...sidePotPanelStyle,
  position: 'relative',
  marginBottom: 4,
  // In-flow in portrait, so it must not stretch to the Pot slot's width —
  // shrink to the rows and centre over the board.
  width: 'fit-content',
  marginInline: 'auto',
  zIndex: 2
};
